import { IChartData, INormalizedChartDate } from "../types";
import { invertRanking } from "./invertRanking";
import { addDaysBeforeDate } from "./addDaysBeforeDate";
import { addDaysAfterDate } from "./addDaysAfterDate";
import { formatChartDate } from "./formatChartDate";
import { createEmptyEntry } from "./createEmptyEntry";
import { ScoresEveryDayBrandRangeValues } from "../../../types/scores";
import { differenceInDays, addDays } from "date-fns";

const TARGET_LENGTH = 16;
const MAX_BEFORE_DAYS = 7;
export const RANKING_MAX = 6;



const getDateKey = (date: Date | string): string => {
  if (date instanceof Date) {
    return date.toISOString().split('T')[0];
  }
  return date.split('T')[0];
};


const applyRangeValues = (
  entry: INormalizedChartDate,
  dateKey: string,
  rangeValues: ScoresEveryDayBrandRangeValues
): void => {
  const currentRangeValues = rangeValues[dateKey];
  
  if (currentRangeValues) {
    entry.visibilityRange = [
      currentRangeValues.visibility.min,
      currentRangeValues.visibility.max
    ];
    entry.sentimentRange = [
      currentRangeValues.segment.min,
      currentRangeValues.segment.max
    ];
  }
};

const createNormalizedEntry = (
  item: IChartData,
  isLast: boolean,
  rangeValues: ScoresEveryDayBrandRangeValues
): INormalizedChartDate => {
  const dateKey = getDateKey(item.created_at);
  const formattedDate = formatChartDate(item.created_at);
  
  const entry: INormalizedChartDate = {
    ...item,
    rankingBar: item.ranking 
      ? invertRanking(item.ranking, Math.max(item.ranking, RANKING_MAX)) 
      : null,
    date: formattedDate,
    isLast,
  };

  applyRangeValues(entry, dateKey, rangeValues);
  
  return entry;
};

const fillMissingDates = (
  currentDate: Date,
  nextDate: Date,
  rangeValues: ScoresEveryDayBrandRangeValues,
  normalizedData: INormalizedChartDate[]
): void => {
  const daysDiff = differenceInDays(nextDate, currentDate);
  
  if (daysDiff > 1) {
    for (let j = 1; j < daysDiff; j++) {
      const missingDate = addDays(currentDate, j);
      const emptyEntry = createEmptyEntry(missingDate, false);
      const missingDateKey = getDateKey(missingDate);
      
      applyRangeValues(emptyEntry, missingDateKey, rangeValues);
      normalizedData.push(emptyEntry);
    }
  }
};

export const normalizeData = (
  data: IChartData[], 
  rangeValues: ScoresEveryDayBrandRangeValues
): INormalizedChartDate[] => {
  if (!data.length) return [];

  const normalizedData: INormalizedChartDate[] = [];
  const dataLength = data.length;

  for (let i = 0; i < dataLength; i++) {
    const currentItem = data[i];
    const isLast = i === dataLength - 1;
    
    const entry = createNormalizedEntry(currentItem, isLast, rangeValues);
    normalizedData.push(entry);

    if (!isLast) {
      const currentDate = new Date(currentItem.created_at);
      const nextDate = new Date(data[i + 1].created_at);
      fillMissingDates(currentDate, nextDate, rangeValues, normalizedData);
    }
  }

  const currentLength = normalizedData.length;
  
  if (currentLength < TARGET_LENGTH) {
    const daysToAdd = TARGET_LENGTH - currentLength;
    const beforeDays = Math.min(MAX_BEFORE_DAYS, Math.ceil(daysToAdd / 2));
    const afterDays = daysToAdd - beforeDays;
    const firstDate = new Date(normalizedData[0].created_at);
    const lastDate = new Date(normalizedData[currentLength - 1].created_at);

    normalizedData.unshift(...addDaysBeforeDate(beforeDays, firstDate));
    normalizedData.push(...addDaysAfterDate(afterDays, lastDate));
  } else if (currentLength > TARGET_LENGTH) {
    const daysToAdd = TARGET_LENGTH - currentLength;
    if (daysToAdd < 0) {
      const firstDate = new Date(normalizedData[0].created_at);
      normalizedData.unshift(...addDaysBeforeDate(Math.abs(daysToAdd), firstDate));
    }
  }

  return normalizedData;
};